import {
  AlertTriangle,
  Banknote,
  CalendarClock,
  PenLine,
  Snowflake,
  StickyNote,
  Weight,
} from "lucide-react";
import type { Task } from "@/api.types.v2";
import { Badge } from "@/components/ui/badge";
import {
  type CareService,
  getTaskCareSignals,
} from "@/features/dispatch/selectors";

const TEAL = "#0d9488";

export const SERVICE_META: Record<
  CareService,
  { icon: typeof PenLine; label: string }
> = {
  signature: { icon: PenLine, label: "Signature required" },
  fragile: { icon: AlertTriangle, label: "Fragile — handle with care" },
  cod: { icon: Banknote, label: "Cash on delivery" },
  appointment: { icon: CalendarClock, label: "Appointment window" },
  heavy: { icon: Weight, label: "Heavy · 2-person lift" },
  cold: { icon: Snowflake, label: "Keep refrigerated" },
};

/** Compact corner icons for rows/markers — a white note chip + teal service
 * chips. Returns null when the task has no care signals. */
export function CareIcons({ task, size = 18 }: { task: Task; size?: number }) {
  const care = getTaskCareSignals(task);
  if (!care.hasAny) return null;
  const icon = size * 0.6;
  return (
    <span className="inline-flex shrink-0 items-center gap-0.5">
      {care.note && (
        <Badge
          variant="outline"
          title={care.note}
          className="rounded border-zinc-200 bg-white p-0 text-zinc-700"
          style={{ width: size, height: size }}
        >
          <StickyNote style={{ width: icon, height: icon }} />
        </Badge>
      )}
      {care.services.map((s) => {
        const Ico = SERVICE_META[s].icon;
        return (
          <Badge
            key={s}
            title={SERVICE_META[s].label}
            className="rounded border-0 p-0 text-white"
            style={{ width: size, height: size, background: TEAL }}
          >
            <Ico style={{ width: icon, height: icon }} />
          </Badge>
        );
      })}
    </span>
  );
}

/** Full-width care rows for detail panels — note (amber) + service chips
 * (teal). Returns null when there's nothing to show. */
export function CareInline({ task }: { task: Task }) {
  const care = getTaskCareSignals(task);
  if (!care.hasAny) return null;
  return (
    <div className="mt-1.5 flex flex-col gap-1">
      {care.note && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-100 bg-amber-50 px-2 py-1.5">
          <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
          <span className="text-[11.5px] leading-snug text-amber-900">
            {care.note}
          </span>
        </div>
      )}
      {care.services.map((s) => {
        const Ico = SERVICE_META[s].icon;
        return (
          <Badge
            key={s}
            variant="outline"
            className="w-full justify-start gap-1.5 rounded-md border-teal-100 bg-teal-50 px-2 py-1 text-[11.5px] font-normal text-teal-800"
          >
            <Ico
              className="shrink-0 text-teal-600"
              style={{ width: 14, height: 14 }}
            />
            {SERVICE_META[s].label}
          </Badge>
        );
      })}
    </div>
  );
}
